import { useState } from 'react'

import {
  useMailPreviewApi,
  useMailSenderApi,
  useTestSendMailApi,
  useUpdateMailSenderApi
} from '@/common/api/modules/ugcMail'
import { SendingMode } from '@/common/api/params/ugcMail'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Heading from '@/components/view/atoms/heading/Heading'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'

/**
 * レビュー依頼メールの差出人と送信モード。
 *
 * **送信モードを画面の一番上に置く。** ここが off である限り1通も出ないので、
 * 「設定したのに送られない」の答えは常にここにある。下の方に隠すと、
 * 差出人だけ埋めて送信された気になる。
 *
 * 差出人アドレスに客先ドメインを使うには W2 側で SPF / DKIM の設定が要る。
 * 未設定のまま送ると迷惑メール扱いになるため、その注意をここに書いておく。
 */

const textInput =
  'shadow-inset text-size-13 w-full rounded-lg border border-border-input bg-input px-3 py-2'

const MODES: { value: SendingMode; label: string; desc: string }[] = [
  { value: 'off', label: '送らない', desc: '1通も送信しません。送信待ちも作りません。' },
  {
    value: 'test',
    label: 'テスト送信先だけに送る',
    desc: '宛先を下のテスト送信先に差し替えて送ります。顧客には届きません。'
  },
  { value: 'live', label: '本番送信', desc: '購入した顧客ご本人へ送ります。' }
]

type Form = {
  fromName: string
  fromEmail: string
  replyTo: string
  mode: SendingMode
  testEmail: string
  dailyLimit: string
  footerText: string
  skipUnavailable: boolean
  groupByOrder: boolean
}

const MailSenderEditor = ({ siteId }: { siteId: number }) => {
  const { data, isLoading, mutate } = useMailSenderApi({ site_id: siteId })
  const { data: preview } = useMailPreviewApi({ site_id: siteId })

  const [form, setForm] = useState<Form | null>(null)
  const [syncedFrom, setSyncedFrom] = useState<unknown>(null)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [testResult, setTestResult] = useState<string | null>(null)
  const [testTo, setTestTo] = useState('')
  const [showPreview, setShowPreview] = useState(false)

  // 取得できたら入力欄へ流す。描画中に同期する（useEffect + setState の連鎖を避ける）
  if (data && syncedFrom !== data) {
    setSyncedFrom(data)
    setForm({
      fromName: data.from_name ?? '',
      fromEmail: data.from_email ?? '',
      replyTo: data.reply_to ?? '',
      mode: data.sending_mode,
      testEmail: data.test_email ?? '',
      dailyLimit: data.daily_limit === null ? '' : String(data.daily_limit),
      footerText: data.footer_text ?? '',
      skipUnavailable: data.skip_unavailable_products ?? true,
      groupByOrder: data.group_by_order ?? true
    })
  }

  const patch = (p: Partial<Form>) => setForm((f) => (f ? { ...f, ...p } : f))

  const { trigger: save, isMutating: saving } = useUpdateMailSenderApi({
    onSuccess: () => {
      setError(null)
      setSaved(true)
      mutate()
    },
    onError: (e) => {
      setError(e instanceof Error ? e.message : '保存できませんでした')
      setSaved(false)
    }
  })

  const { trigger: testSend, isMutating: testing } = useTestSendMailApi({
    onSuccess: (r) => {
      setTestResult(r.ok ? `${r.to} へ送信しました。受信箱を確認してください。` : (r.error ?? '送信できませんでした'))
      mutate()
    },
    onError: (e) => setTestResult(e instanceof Error ? e.message : '送信できませんでした')
  })

  if (isLoading || !data || !form) {
    return (
      <Area>
        <AreaContent>
          <Text color={TextColor.SECONDARY}>読み込み中...</Text>
        </AreaContent>
      </Area>
    )
  }

  const onSave = () => {
    setSaved(false)
    save({
      site_id: siteId,
      from_name: form.fromName.trim() || null,
      from_email: form.fromEmail.trim() || null,
      reply_to: form.replyTo.trim() || null,
      sending_mode: form.mode,
      test_email: form.testEmail.trim() || null,
      daily_limit: form.dailyLimit.trim() === '' ? null : Number(form.dailyLimit),
      footer_text: form.footerText.trim() || null,
      skip_unavailable_products: form.skipUnavailable,
      group_by_order: form.groupByOrder
    })
  }

  return (
    <>
      <Area>
        <Heading subText='ここが「送らない」の間は、送信待ちも作られません。'>
          送信モード
        </Heading>
        <AreaContent>
          <div className='flex flex-col gap-3'>
            {MODES.map((m) => (
              <div key={m.value} className='flex flex-col gap-1'>
                <label className='flex cursor-pointer items-start gap-3'>
                  <input
                    type='radio'
                    name='sending-mode'
                    className='mt-1'
                    checked={form.mode === m.value}
                    onChange={() => patch({ mode: m.value })}
                  />
                  <div className='flex flex-col gap-0.5'>
                    <div className='flex items-center gap-2'>
                      <Text size={13} weight='bold'>
                        {m.label}
                      </Text>
                      {data.sending_mode === m.value && (
                        <Badge size={10} badgeType={BadgeType.SUCCESS}>
                          現在の設定
                        </Badge>
                      )}
                    </div>
                    <Text size={12} color={TextColor.SECONDARY}>
                      {m.desc}
                    </Text>
                  </div>
                </label>

                {/* 差し替え先はこのモードでしか意味を持たない設定なので、説明のすぐ下に置く。
                    「いま1通だけ送る」（下の動作確認）とは別物——あちらはモードに関係なく使える */}
                {m.value === 'test' && (
                  <div className='pl-7'>
                    <label className='flex flex-col gap-1 md:max-w-md'>
                      <Text size={12}>差し替え先のアドレス</Text>
                      <input
                        className={textInput}
                        value={form.testEmail}
                        placeholder='例）tanaka@example.co.jp'
                        onChange={(e) => patch({ testEmail: e.target.value })}
                      />
                      <Text size={10} color={TextColor.TERTIARY}>
                        このモードの間は、すべてのメールがこの宛先に届きます。下の「保存する」で保存してください。
                      </Text>
                    </label>
                  </div>
                )}
              </div>
            ))}
          </div>

          {data.problems.length > 0 && (
            <div className='mt-4 rounded-lg border border-border-caution bg-fill-caution px-4 py-3'>
              <Text size={12} weight='bold'>
                いまは送信されません
              </Text>
              <ul className='mt-1 list-disc pl-5'>
                {data.problems.map((p) => (
                  <li key={p}>
                    <Text size={12} color={TextColor.SECONDARY}>
                      {p}
                    </Text>
                  </li>
                ))}
              </ul>
              {!data.smtp_configured && (
                <div className='mt-2'>
                  <Text size={10} color={TextColor.TERTIARY}>
                    共通メール配信サーバの設定はサイト側では変更できません。W2の担当までご連絡ください。
                  </Text>
                </div>
              )}
            </div>
          )}

          <div className='mt-4'>
            <Text size={12} color={TextColor.SECONDARY}>
              本日の送信数: {data.sent_today} 通
              {data.daily_limit ? `（上限 ${data.daily_limit} 通）` : '（上限なし・15分ごとに分散送信）'}
            </Text>
          </div>
        </AreaContent>
      </Area>

      <Area>
        <Heading subText='送る相手を絞ります。商品ごと・カテゴリごとの細かい指定は「送信タイミングと文面の設定」で行います。'>
          送信対象
        </Heading>
        <AreaContent>
          <label className='flex cursor-pointer items-start gap-3'>
            <input
              type='checkbox'
              className='mt-1'
              checked={form.skipUnavailable}
              onChange={(e) => patch({ skipUnavailable: e.target.checked })}
            />
            <div className='flex flex-col gap-0.5'>
              <Text size={13} weight='bold'>
                販売終了・非公開の商品には送らない
              </Text>
              <Text size={12} color={TextColor.SECONDARY}>
                いま買えない商品にレビューを依頼しません。書いてもらっても、読んだ人が購入できないためです。
                判定はW2の表示設定（有効フラグ・検索時表示区分・外部レコメンド利用・表示期間）に従います。
              </Text>
              <Text size={10} color={TextColor.TERTIARY}>
                季節商品などで「また販売するので今のうちに声を集めたい」場合は、
                チェックを外すと販売終了後も依頼します。
              </Text>
            </div>
          </label>

          <label className='mt-4 flex cursor-pointer items-start gap-3'>
            <input
              type='checkbox'
              className='mt-1'
              checked={form.groupByOrder}
              onChange={(e) => patch({ groupByOrder: e.target.checked })}
            />
            <div className='flex flex-col gap-0.5'>
              <Text size={13} weight='bold'>
                1回の注文はまとめて1通で送る
              </Text>
              <Text size={12} color={TextColor.SECONDARY}>
                1回の注文で複数の商品を購入された場合も、メールは1通にまとめます。
                投稿フォームには購入された商品が並び、書ける商品だけ書いていただけます。
              </Text>
              <Text size={10} color={TextColor.TERTIARY}>
                チェックを外すと商品ごとに1通ずつ送ります。商品によって文面を分けたい場合にお使いください。
              </Text>
            </div>
          </label>
        </AreaContent>
      </Area>

      <Area>
        <Heading subText='メールの差出人欄に表示される内容です。'>差出人</Heading>
        <AreaContent>
          <div className='grid gap-4 md:grid-cols-2'>
            <label className='flex flex-col gap-1'>
              <Text size={12}>差出人の表示名</Text>
              <input
                className={textInput}
                value={form.fromName}
                placeholder='例）W2コーポレートサイト'
                onChange={(e) => patch({ fromName: e.target.value })}
              />
            </label>
            <label className='flex flex-col gap-1'>
              <Text size={12}>差出人アドレス</Text>
              <input
                className={textInput}
                value={form.fromEmail}
                placeholder='例）review@example.co.jp'
                onChange={(e) => patch({ fromEmail: e.target.value })}
              />
              {/* 送信認証の注意は入力欄の直下に置く。離れた位置に囲みで出すと、
                  アドレスを決める時点で読まれない */}
              <Text size={10} color={TextColor.TERTIARY}>
                自社ドメインのアドレスを使う場合は、W2側で送信認証（SPF /
                DKIM）の設定が必要です。設定前に使うと、届かない・迷惑メールに入ることがあります。未設定のうちは、W2から案内されたアドレスをお使いください。
              </Text>
            </label>
            <label className='flex flex-col gap-1'>
              <Text size={12}>返信先アドレス</Text>
              <input
                className={textInput}
                value={form.replyTo}
                placeholder='お客様が返信したときの宛先'
                onChange={(e) => patch({ replyTo: e.target.value })}
              />
            </label>
            <label className='flex flex-col gap-1'>
              <Text size={12}>1日の送信上限（通）</Text>
              <input
                className={textInput}
                type='number'
                min={1}
                value={form.dailyLimit}
                placeholder='空欄＝制限なし'
                onChange={(e) => patch({ dailyLimit: e.target.value })}
              />
              <Text size={10} color={TextColor.TERTIARY}>
                通常は空欄のままにしてください。送信は15分ごとに小分けで行われ、負荷は自動で分散されます。
              </Text>
            </label>
          </div>

          <div className='mt-4'>
            <label className='flex flex-col gap-1'>
              <Text size={12}>本文末尾（署名・お問い合わせ先）</Text>
              <textarea
                className={`${textInput} min-h-[80px]`}
                value={form.footerText}
                placeholder={'例）\n株式会社○○ カスタマーサポート\nsupport@example.co.jp'}
                onChange={(e) => patch({ footerText: e.target.value })}
              />
            </label>
          </div>

          <div className='mt-4 flex items-center gap-3'>
            <Button color={ButtonColor.PRIMARY} loading={saving} disabled={saving} onClick={onSave}>
              保存する
            </Button>
            {saved && (
              <Text size={12} color={TextColor.SECONDARY}>
                保存しました
              </Text>
            )}
            {error && (
              <Text size={12} color={TextColor.CRITICAL}>
                {error}
              </Text>
            )}
          </div>
        </AreaContent>
      </Area>

      <Area>
        <Heading subText='送信モードに関わらず、いつでも1通だけ送って確かめられます。本番送信を続けたままでも実行でき、この1通は開封率などの集計には入りません。'>
          動作確認
        </Heading>
        <AreaContent>
          <div className='flex flex-col gap-3'>
            <div className='flex flex-wrap items-center gap-3'>
              <input
                className={`${textInput} md:max-w-xs`}
                value={testTo}
                placeholder='送り先（空欄なら差し替え先のアドレス）'
                onChange={(e) => setTestTo(e.target.value)}
              />
              <Button
                color={ButtonColor.SECONDARY}
                loading={testing}
                disabled={testing}
                onClick={() => {
                  setTestResult(null)
                  testSend({ site_id: siteId, to: testTo.trim() || null })
                }}
              >
                いま1通だけ送る
              </Button>
              <Button color={ButtonColor.TERTIARY} onClick={() => setShowPreview((v) => !v)}>
                {showPreview ? 'プレビューを閉じる' : '文面を確認する'}
              </Button>
            </div>

            {testResult && (
              <Text size={12} color={TextColor.SECONDARY}>
                {testResult}
              </Text>
            )}

            {showPreview && preview && (
              <div className='flex flex-col gap-2'>
                <Text size={12} color={TextColor.SECONDARY}>
                  件名: {preview.subject}
                </Text>
                <Text size={10} color={TextColor.TERTIARY}>
                  差し込み例: {preview.sample_customer_name}様 / {preview.sample_product_name}
                </Text>
                <div className='overflow-x-auto rounded-lg border border-border-primary'>
                  {/* 実際に送られる HTML をそのまま描く。プレビュー用に組み直すと実物とずれる */}
                  <iframe
                    title='メール本文のプレビュー'
                    srcDoc={preview.html}
                    sandbox=''
                    className='h-[520px] w-full min-w-[560px] bg-white'
                  />
                </div>
              </div>
            )}
          </div>
        </AreaContent>
      </Area>
    </>
  )
}

export default MailSenderEditor
